/**
 * Costs: what the AI work cost and where it went ("token economics", 0.90.0). The data column (Layout.tsx): the period
 * and the filters in the header, then four views in the address (`?view=`):
 *
 *   Overview     spend at a glance (spend, tokens, calls, cache efficiency), usage and spend over time, how it is
 *                allocated by team, source and kind of work, the highest spend named (agents, jobs, people), each
 *                budget's standing, and unusual spend (GET /admin/v1/costs/economics)
 *   Breakdown    one table by any grouping: agent, session, repository, source, team, project, person, model, machine
 *                or business area, each row opening its thing (GET /admin/v1/costs?by=), and cost per outcome
 *   Statements   the month's chargeback statement, invoices and the schedule (Enterprise)
 *   Prices       the organisation's price list (Enterprise)
 *
 * Budgets stay their own page: setting limits is a different job from reading spend, and the page holds a form and a
 * tree of its own. The overview shows each budget's standing against the same period and links there.
 *
 * Money follows the ledger (costs.ts): spend is metered spend; calls on paid seats show as their value at list price
 * beside it, never added in; calls with no known price are counted, not priced. Amounts are in the organisation's
 * currency (money.tsx). On AI Cloud the same page serves a Teams plan, without business areas, statements or prices.
 */

import { BUDGET_STATE_TEXT, type BudgetStanding, type CostGrouping, type CostLine, type CostRank } from '@aioe/shared';
import { dateMedium } from '@aioe/shared/wording/dateTime';
import { Calendar, ChartColumn, ChartPie, CircleDollar, Database, Funnel, Layers, ListOl, Persons, Receipt, Sparkles } from '@gravity-ui/icons';
import { Button, Popover } from '@heroui/react';
import { Segment } from '@heroui-pro/react';
import { useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { ApiError, routes } from '../api/client.ts';
import { Outcomes, UnusualSpend } from '../components/costInsights.tsx';
import { Donut, RankBars, Sparkline, TrendChart, type TrendSeries } from '../components/costs/EconomicsCharts.tsx';
import { changeWords, compact, filterCount, filtersOf, measureOf, percentWords, rankHref, RANGE_KEYS, RANGE_LABELS, RANGE_WORDS, readView, spanWords, valueOf, writeView, type CostView, type Measure, type RangeKey } from '../components/costs/costModel.ts';
import { Chargeback } from '../components/costs/Statements.tsx';
import { Invoices, StatementSchedule } from '../components/invoices.tsx';
import { Pricing } from '../components/pricing.tsx';
import { SubjectName } from '../components/SubjectPicker.tsx';
import { MemberFace } from '../components/teams/MemberFace.tsx';
import { ArtEmpty, ArtSection, ChoiceSelect, ErrorNote, inputClass, ListBlock, ListGroup, PageColumn, PageHeader, Stat, StatusMark } from '../components/ui.tsx';
import { VIEW_TONES, type ArtTone } from '../components/ui/Art.tsx';
import { Hint } from '../components/ui/Hint.tsx';
import { Explainer } from '../components/ui/Layout.tsx';
import { PartBoundary } from '../components/ui/PartBoundary.tsx';
import { isCloud } from '../edition.ts';
import { useLoad } from '../hooks.ts';
import { useNames } from '../names.ts';
import { ProjectChips, ProjectPicker } from '../components/ProjectPicker.tsx';
import { useMoney } from '../money.tsx';

type Tab = 'overview' | 'breakdown' | 'statements' | 'prices';
const TABS: Record<Tab, string> = { overview: 'Overview', breakdown: 'Breakdown', statements: 'Statements', prices: 'Prices' };
const tabsHere = (): Tab[] => (isCloud() ? ['overview', 'breakdown'] : ['overview', 'breakdown', 'statements', 'prices']);

/** The groupings a breakdown offers, in the order people reach for them. */
const GROUPS: Record<CostGrouping, { label: string; column: string; none: string }> = {
  member: { label: 'Agent', column: 'Agent', none: 'No agent recorded' },
  session: { label: 'Session or run', column: 'Session or run', none: 'Calls outside any session' },
  repository: { label: 'Repository', column: 'Repository', none: 'No repository recorded' },
  source: { label: 'Source', column: 'Source', none: 'Not recorded' },
  team: { label: 'Team', column: 'Team', none: 'No team' },
  project: { label: 'Project', column: 'Project', none: 'No project' },
  person: { label: 'Person', column: 'Person', none: 'No person recorded' },
  model: { label: 'Model', column: 'Model', none: 'Unknown' },
  device: { label: 'Machine', column: 'Machine', none: 'Unknown' },
  area: { label: 'Business area', column: 'Business area', none: 'No business area' },
};
const groupsHere = (): CostGrouping[] => (Object.keys(GROUPS) as CostGrouping[]).filter((g) => !(isCloud() && g === 'area'));
const isGrouping = (g: string | null): g is CostGrouping => Boolean(g) && groupsHere().includes(g as CostGrouping);

const forbidden = (e: unknown) => e instanceof ApiError && e.status === 403;

export function CostsPage() {
  const tone = VIEW_TONES.costs!;
  const { name: currencyName } = useMoney();
  const [params, setParams] = useSearchParams();
  const view = readView(params);
  const tabs = tabsHere();
  const tab: Tab = tabs.includes(params.get('view') as Tab) ? (params.get('view') as Tab) : 'overview';
  const setTab = (t: Tab) => setParams((p) => { const n = new URLSearchParams(p); if (t === 'overview') n.delete('view'); else n.set('view', t); return n; }, { replace: true });
  const setView = (v: CostView) => setParams((p) => writeView(p, v), { replace: true });
  // The period is worked out once per address, so a page left open keeps asking for the same thing.
  const key = FILTER_KEY(params);
  const filters = useMemo(() => filtersOf(view, new Date()), [key]); // eslint-disable-line react-hooks/exhaustive-deps
  const reading = tab === 'overview' || tab === 'breakdown';
  return (
    <PageColumn width="data">
      <PageHeader
        tone={tone}
        icon={<ChartColumn />}
        title="Costs"
        subtitle={`What the AI work cost in ${currencyName}, and where it went: by team, agent, session, project and repository.`}
        actions={
          reading ? (
            <>
              <PeriodPicker view={view} onChange={setView} />
              <FilterButton view={view} filters={filters} onChange={setView} />
            </>
          ) : undefined
        }
      />
      <Explainer id="costs" tone={tone} icon={<ChartColumn />} title="How costs are counted" summary="Spend is what providers bill per token; paid seats show beside it, never added in">
        <p>Every model call a workbench or node makes is reported with its tokens and its price. Spend is what a provider bills per token. Calls on a paid seat (a subscription) cost nothing more, so they show as what they would have cost at list price, beside the spend and never added to it. A call with no known price is counted, but not in any amount.</p>
        <p>Agents, sessions, repositories, sources and kinds of work come from the newest AI Workbench, which says which agent made each call and what for. Calls from older builds count in the totals but not against an agent.</p>
        <p>Cache efficiency is the share of prompt tokens read from a provider's prompt cache: the higher it is, the less the same context costs again.</p>
      </Explainer>
      <Segment aria-label="Costs view" size="sm" selectedKey={tab} onSelectionChange={(k) => setTab(String(k) as Tab)} className="w-fit max-w-full">
        {tabs.map((t) => (
          <Segment.Item key={t} id={t}>
            {TABS[t]}
          </Segment.Item>
        ))}
      </Segment>
      <PartBoundary key={tab} name={`Costs ${TABS[tab].toLowerCase()}`} tone={tone}>
        {tab === 'overview' ? <Overview tone={tone} view={view} filters={filters} /> : null}
        {tab === 'breakdown' ? <Breakdown tone={tone} filters={filters} by={isGrouping(params.get('by')) ? (params.get('by') as CostGrouping) : 'member'} onBy={(by) => setParams((p) => { const n = new URLSearchParams(p); n.set('by', by); return n; }, { replace: true })} /> : null}
        {tab === 'statements' ? (
          <div className="grid gap-4">
            <Chargeback />
            <Invoices />
            <StatementSchedule />
          </div>
        ) : null}
        {tab === 'prices' ? <Pricing /> : null}
      </PartBoundary>
    </PageColumn>
  );
}

/** The address's filters as one string: what the period is worked out from. */
const FILTER_KEY = (p: URLSearchParams) => ['range', 'since', 'until', 'billing', 'area', 'project', 'team', 'repository', 'model'].map((k) => p.get(k) ?? '').join('|');

const dayWords = (iso: string) => dateMedium(iso, { year: true });
const dateInput = (iso: string | undefined) => (iso ? iso.slice(0, 10) : '');

/** The period: a choice of ranges, and a range of one's own in a popover. */
function PeriodPicker({ view, onChange }: { view: CostView; onChange: (v: CostView) => void }) {
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState(dateInput(view.since));
  const [to, setTo] = useState(dateInput(view.until));
  const valid = from && to && from <= to;
  const CUSTOM = '__custom';
  const options = { ...Object.fromEntries(RANGE_KEYS.map((k) => [k, k.endsWith('d') ? `Last ${k.replace('d', ' days')}` : RANGE_LABELS[k]])), ...(view.since ? { [CUSTOM]: `${dayWords(view.since)} to ${dayWords(view.until ?? new Date().toISOString())}` } : {}) } as Record<string, string>;
  return (
    <div className="flex items-center gap-1.5">
      <ChoiceSelect label="Period" className="w-44 max-w-full" value={view.since ? CUSTOM : (view.range ?? '30d')} options={options} onChange={(k) => k !== CUSTOM && onChange({ ...view, since: undefined, until: undefined, range: k as RangeKey })} />
      <Popover isOpen={open} onOpenChange={setOpen}>
        <Hint text="A range of your own">
          <Button size="sm" variant={view.since ? 'secondary' : 'ghost'} isIconOnly aria-label="A range of your own">
            <Calendar className="size-4" />
          </Button>
        </Hint>
        <Popover.Content placement="bottom end" className="w-[17rem] max-w-[calc(100vw-1rem)]">
          <Popover.Dialog className="grid gap-3 p-3" aria-label="A range of your own">
            <label className="grid gap-1 text-xs">
              <span className="font-medium">From</span>
              <input type="date" className={inputClass} value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
            </label>
            <label className="grid gap-1 text-xs">
              <span className="font-medium">To</span>
              <input type="date" className={inputClass} value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
            </label>
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="tertiary" onPress={() => setOpen(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                isDisabled={!valid}
                onPress={() => {
                  // Whole UTC days, as the ledger counts them: from the first one's start to the last one's end.
                  const until = new Date(Date.parse(`${to}T00:00:00Z`) + 86_400_000).toISOString();
                  onChange({ ...view, range: undefined, since: `${from}T00:00:00.000Z`, until: until > new Date().toISOString() ? new Date().toISOString() : until });
                  setOpen(false);
                }}
              >
                Show
              </Button>
            </div>
          </Popover.Dialog>
        </Popover.Content>
      </Popover>
    </div>
  );
}

const ANY = '__any';
const BILLING_WORDS = { all: 'Every call', metered: 'Metered calls only', subscription: 'Calls on paid seats only' } as const;

/** Which calls, and narrowing to an area, a project, a team or specialist, a repository or a model: one control, its choices in a popover. */
function FilterButton({ view, filters, onChange }: { view: CostView; filters: ReturnType<typeof filtersOf>; onChange: (v: CostView) => void }) {
  const [open, setOpen] = useState(false);
  const count = filterCount(view);
  // The choices load when the popover opens: the repositories and models are those with calls in the period.
  const lists = useLoad(
    async () => {
      if (!open) return undefined;
      const period = { since: filters.since, until: filters.until };
      const [projects, teams, specialists, areas, repos, models] = await Promise.all([
        Promise.resolve([] as Array<{ id: string; name: string }>),
        routes.teams().catch(() => []),
        routes.teams('specialist').catch(() => []),
        isCloud() ? Promise.resolve([]) : routes.areas().catch(() => []),
        routes.costs({ ...period, by: 'repository', top: 100, ...(view.projectId ? { projectId: view.projectId } : {}) }).then((r) => r.lines.filter((l) => !l.rest && l.rootId)).catch(() => [] as CostLine[]),
        routes.costs({ ...period, by: 'model', top: 50 }).then((r) => r.lines.filter((l) => !l.rest && l.key)).catch(() => [] as CostLine[]),
      ]);
      return { projects, teams: [...teams, ...specialists.filter((s) => !teams.some((t) => t.id === s.id))], areas, repos, models };
    },
    [open, filters.since, filters.until, view.projectId],
  );
  const l = lists.data;
  const set = (patch: Partial<CostView>) => onChange({ ...view, ...patch });
  const pick = (v: string) => (v === ANY ? undefined : v);
  return (
    <Popover isOpen={open} onOpenChange={setOpen}>
      <Button size="sm" variant={count ? 'secondary' : 'ghost'} aria-label={count ? `Filters: ${count} set` : 'Filters'}>
        <Funnel className="size-4" />
        {count ? `Filters · ${count}` : 'Filters'}
      </Button>
      <Popover.Content placement="bottom end" className="w-80 max-w-[calc(100vw-1rem)]">
        <Popover.Dialog className="grid gap-3 p-3" aria-label="Filter the costs">
          <Field label="Calls" hint="Spend is always metered; calls on paid seats count as what they would have cost.">
            <ChoiceSelect label="Calls" className="w-full" value={view.billing} options={BILLING_WORDS} onChange={(b) => set({ billing: b })} />
          </Field>
          {!isCloud() && (l?.areas.length ?? 0) > 0 ? (
            <Field label="Business area">
              <ChoiceSelect label="Business area" className="w-full" value={view.areaId ?? ANY} options={{ [ANY]: 'Every business area', ...Object.fromEntries(l!.areas.map((a) => [a.id, a.name])) }} onChange={(v) => set({ areaId: pick(v) })} />
            </Field>
          ) : null}
          <Field label="Project">
            <div className="grid gap-2">
              <span className="text-sm text-muted">Project</span>
              {view.projectId ? <ProjectChips ids={[view.projectId]} onRemove={() => set({ projectId: undefined, repository: undefined })} /> : <ProjectPicker label="Search projects" placeholder="Every project: search to choose one" onPick={(p) => set({ projectId: p.id, repository: undefined })} />}
            </div>
          </Field>
          <Field label="Repository">
            <ChoiceSelect label="Repository" className="w-full" value={view.repository ?? ANY} options={{ [ANY]: 'Every repository', ...Object.fromEntries((l?.repos ?? []).map((r) => [r.key, r.detail && !view.projectId ? `${r.name} · ${r.detail}` : (r.name ?? r.key)])) }} onChange={(v) => set({ repository: pick(v) })} isDisabled={!l} />
          </Field>
          <Field label="Team or specialist">
            <ChoiceSelect label="Team or specialist" className="w-full" value={view.teamId ?? ANY} options={{ [ANY]: 'Every team', ...Object.fromEntries((l?.teams ?? []).map((t) => [t.id, t.kind === 'specialist' ? `${t.name} (specialist)` : t.name])) }} onChange={(v) => set({ teamId: pick(v) })} isDisabled={!l} />
          </Field>
          <Field label="Model">
            <ChoiceSelect label="Model" className="w-full" value={view.model ?? ANY} options={{ [ANY]: 'Every model', ...Object.fromEntries((l?.models ?? []).map((m) => [m.key, m.key])) }} onChange={(v) => set({ model: pick(v) })} isDisabled={!l} />
          </Field>
          <ErrorNote error={lists.error} />
          {count ? (
            <div className="flex justify-end">
              <Button size="sm" variant="tertiary" onPress={() => onChange({ range: view.range, since: view.since, until: view.until, billing: 'all' } as CostView)}>
                Clear the filters
              </Button>
            </div>
          ) : null}
        </Popover.Dialog>
      </Popover.Content>
    </Popover>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="grid gap-1 text-xs">
      <span className="font-medium">{label}</span>
      {children}
      {hint ? <span className="text-muted">{hint}</span> : null}
    </div>
  );
}

// ── Overview ────────────────────────────────────────────────────────

type Trend = 'spend' | 'tokens' | 'calls';

function Overview({ tone, view, filters }: { tone: ArtTone; view: CostView; filters: ReturnType<typeof filtersOf> }) {
  const { format } = useMoney();
  const e = useLoad(() => routes.costEconomics(filters).catch((x: unknown) => (forbidden(x) ? null : Promise.reject(x))), [filters], 120_000, { whenVisible: true });
  const d = e.data;
  const [trend, setTrend] = useState<Trend>('spend');
  const [chosen, setMeasure] = useState<Measure>();
  const measure = chosen ?? measureOf(d?.total);
  const money = (n: number) => format(n);
  const fmt = (n: number) => (measure === 'usd' ? money(n) : compact(n));
  const words = view.since ? `${dayWords(view.since)} to ${dayWords(view.until ?? new Date().toISOString())}` : RANGE_WORDS[view.range ?? '30d'];
  if (d === null) {
    return (
      <ArtEmpty tone={tone} icon={<ChartColumn />} title="Costs are for administrators">
        {isCloud() ? "Your team's owners and administrators see what the work cost. Ask one of them, or AI Cloud support." : 'Administrators, portfolio managers and business area managers (for their areas) read costs.'}
      </ArtEmpty>
    );
  }
  if (!d) return e.error ? <ErrorNote error={e.error} /> : <p className="text-sm text-muted">Reading the costs…</p>;
  const t = d.total;
  const seats = t.subscriptionUsd > 0;
  const series: TrendSeries[] =
    trend === 'spend'
      ? [{ label: 'Spend', colour: 'var(--series-1)', value: (p) => p.usd }, ...(seats ? [{ label: 'Paid seats, at list price', colour: 'var(--series-2)', value: (p: (typeof d.series)[number]) => p.subscriptionUsd }] : [])]
      : trend === 'tokens'
        ? [
            { label: 'Input', colour: 'var(--series-1)', value: (p) => p.promptTokens },
            { label: 'Output', colour: 'var(--series-2)', value: (p) => p.completionTokens },
          ]
        : [{ label: 'Calls', colour: 'var(--series-1)', value: (p) => p.calls }];
  const nothing = t.calls === 0;
  return (
    <div className="econ">
      <ErrorNote error={e.error} />
      <div className="art-stats">
        <Stat tone={tone} icon={<CircleDollar />} label="Spend" value={money(t.usd)} detail={[changeWords(t.usd, d.previous.usd), seats ? `${money(t.subscriptionUsd)} on paid seats` : ''].filter(Boolean).join(' · ') || 'Metered spend'} />
        <Stat tone={tone} icon={<Database />} label="Tokens" value={compact(t.tokens)} detail={`${compact(t.promptTokens)} in · ${compact(t.completionTokens)} out`} />
        <Stat tone={tone} icon={<Layers />} label="Calls" value={compact(t.calls)} detail={t.unpricedCalls ? `${compact(t.unpricedCalls)} with no known price` : t.calls && t.usd ? `${format(t.usd / t.calls, { digits: 4 })} a call` : 'Model calls in the period'} />
        <Stat tone={tone} icon={<Sparkles />} label="Cache efficiency" value={percentWords(d.cache.efficiency)} detail={d.cache.efficiency === null ? 'No prompt tokens yet' : d.cache.previous !== null ? `${percentWords(d.cache.previous)} the period before` : `${compact(t.cacheReadTokens ?? 0)} prompt tokens from cache`} />
      </div>
      {d.notes.length ? <p className="econ__note">{d.notes.join(' ')}</p> : null}
      {nothing ? (
        <ArtEmpty tone={tone} icon={<ChartColumn />} title={`Nothing recorded for ${words}`}>
          Workbenches and nodes report every model call with its tokens and price. Pick a longer period{filterCount(view) ? ', or clear the filters' : ''}.
        </ArtEmpty>
      ) : (
        <>
          <ArtSection
            tone={tone}
            icon={<ChartColumn />}
            title="Usage and spend over time"
            hint={`Per ${d.bucket}, ${words}`}
            actions={
              <Segment aria-label="Show" size="sm" selectedKey={trend} onSelectionChange={(k) => setTrend(String(k) as Trend)}>
                <Segment.Item id="spend">Spend</Segment.Item>
                <Segment.Item id="tokens">Tokens</Segment.Item>
                <Segment.Item id="calls">Calls</Segment.Item>
              </Segment>
            }
          >
            <TrendChart points={d.series} series={series} bucket={d.bucket} label={trend === 'spend' ? 'Spend' : trend === 'tokens' ? 'Tokens' : 'Calls'} format={trend === 'spend' ? money : compact} />
          </ArtSection>
          <ArtSection
            tone={tone}
            icon={<ChartPie />}
            title="Usage allocation"
            hint={measure === 'usd' ? 'Share of the spend' : 'Share of the tokens: the period had no metered spend'}
            actions={
              <Segment aria-label="Weigh by" size="sm" selectedKey={measure} onSelectionChange={(k) => setMeasure(String(k) as Measure)}>
                <Segment.Item id="usd">Spend</Segment.Item>
                <Segment.Item id="tokens">Tokens</Segment.Item>
              </Segment>
            }
          >
            <div className="econ__three">
              <Allocation title="By team" by="team" none="No team" slices={d.allocation.team} measure={measure} format={fmt} total={valueOf(t, measure)} />
              <Allocation title="By source" by="source" slices={d.allocation.source} measure={measure} format={fmt} total={valueOf(t, measure)} />
              <Allocation title="By kind of work" by="work" slices={d.allocation.work} measure={measure} format={fmt} total={valueOf(t, measure)} />
            </div>
          </ArtSection>
          <ArtSection tone={tone} icon={<ListOl />} title="Highest spend" hint={measure === 'usd' ? 'Ranked by spend' : 'Ranked by tokens'}>
            <div className="econ__three">
              <Ranking title="Agents" by="member" ranks={d.highest.agents} measure={measure} format={fmt} empty="No machine says which agent made each call yet." />
              <Ranking title="Jobs" by="session" ranks={d.highest.jobs} measure={measure} format={fmt} empty="No sessions or runs reported in this period." name={(r) => <JobName r={r} />} />
              <Ranking title="People" by="person" ranks={d.highest.people} measure={measure} format={fmt} empty="No person recorded on these calls." name={(r) => <SubjectName id={r.key} fallback={r.key} />} />
            </div>
          </ArtSection>
          {!isCloud() ? <BudgetStandings tone={tone} view={view} /> : null}
          {!isCloud() ? <UnusualSpend since={filters.since} /> : null}
        </>
      )}
    </div>
  );
}

function Allocation({ title, by, slices, measure, format, total, none }: { title: string; by: CostGrouping | 'work'; slices: CostRank[]; measure: Measure; format: (n: number) => string; total: number; none?: string }) {
  const sorted = [...slices].sort((a, b) => Number(Boolean(a.rest)) - Number(Boolean(b.rest)) || valueOf(b, measure) - valueOf(a, measure));
  return (
    <div className="min-w-0">
      <div className="econ__col-title">{title}</div>
      <Donut slices={sorted} by={by} measure={measure} format={format} title={title} total={total} {...(none ? { none } : {})} />
    </div>
  );
}

function Ranking({ title, by, ranks, measure, format, empty, name }: { title: string; by: CostGrouping; ranks: CostRank[]; measure: Measure; format: (n: number) => string; empty: string; name?: (r: CostRank) => ReactNode }) {
  const [params] = useSearchParams();
  const all = new URLSearchParams(params);
  all.set('view', 'breakdown');
  all.set('by', by);
  const sorted = [...ranks].sort((a, b) => valueOf(b, measure) - valueOf(a, measure));
  return (
    <div className="min-w-0">
      <div className="econ__col-title">
        <span>{title}</span>
        {ranks.length ? <Link to={`?${all}`}>See all</Link> : null}
      </div>
      <RankBars ranks={sorted} by={by} measure={measure} format={format} empty={empty} {...(name ? { name } : {})} />
    </div>
  );
}

/** A job for people: what it was and when, never its id. */
function JobName({ r }: { r: CostRank }) {
  const when = r.firstAt ? dateMedium(r.firstAt, { year: false }) : undefined;
  const span = spanWords(r.firstAt, r.lastAt);
  return (
    <>
      {r.name ?? 'A job'}
      {when ? <span className="econ-rank__detail"> · {when}{span ? `, ${span}` : ''}</span> : null}
    </>
  );
}

const STATE_MARK: Record<BudgetStanding['state'], 'connected' | 'medium' | 'high' | 'error'> = { ok: 'connected', warning: 'medium', throttled: 'high', blocked: 'error' };

/** Each budget that bears on what the page shows: spent against its limit this period, and where the week's pace takes it. */
function BudgetStandings({ tone, view }: { tone: ArtTone; view: CostView }) {
  const { format } = useMoney();
  const b = useLoad(() => routes.budgets().catch((x: unknown) => (forbidden(x) ? null : Promise.reject(x))), [], 120_000, { whenVisible: true });
  const teams = useLoad(() => Promise.all([routes.teams().catch(() => []), routes.teams('specialist').catch(() => [])]).then(([a, s]) => new Map([...a, ...s].map((x) => [x.id, x.name] as const))), []);
  // The budgets on screen name their projects by id: asked for those, with the area each sits in.
  const named = useNames([...(b.data ?? []).flatMap((s) => (s.budget.scope.type === 'project' ? [s.budget.scope.id] : [])), view.projectId]);
  const projects = { data: new Map([...named.projects].map(([id, name]) => [id, { id, name, businessAreaId: named.projectAreas.get(id) }] as const)) };
  if (!b.data) return b.error ? <ErrorNote error={b.error} /> : null;
  const project = view.projectId ? projects.data?.get(view.projectId) : undefined;
  // With a team or a project chosen, the budgets on it and those above it; otherwise every one.
  const bears = (s: BudgetStanding) => {
    const sc = s.budget.scope;
    if (view.teamId) return sc.type === 'tenant' || (sc.type === 'team' && sc.id === view.teamId);
    if (view.projectId) return sc.type === 'tenant' || (sc.type === 'project' && sc.id === view.projectId) || (sc.type === 'area' && sc.id === project?.businessAreaId);
    if (view.areaId) return sc.type === 'tenant' || (sc.type === 'area' && sc.id === view.areaId);
    return true;
  };
  const rows = b.data.filter(bears).sort((x, y) => y.percent - x.percent);
  const scopeWords = (s: BudgetStanding) => {
    const sc = s.budget.scope;
    if (sc.type === 'tenant') return 'The organisation';
    if (sc.type === 'team') return teams.data?.get(sc.id) ?? 'A team';
    if (sc.type === 'project') return projects.data?.get(sc.id)?.name ?? 'A project';
    if (sc.type === 'area') return 'A business area';
    return <SubjectName id={sc.id} fallback={sc.id} />;
  };
  return (
    <ListGroup
      title="Budgets"
      count={rows.length || undefined}
      description="Each budget's spend this period against its limit; the mark is where the last seven days' pace takes it by the period's end."
      actions={
        <Link to="/budgets" className="text-xs text-accent hover:underline">
          Manage budgets
        </Link>
      }
      empty={
        rows.length === 0 ? (
          <ArtEmpty tone={tone} icon={<Receipt />} compact title={b.data.length ? 'No budget bears on this view' : 'No budgets yet'} action={<Link to="/budgets" className="text-sm text-accent hover:underline">Set a budget</Link>}>
            A budget is a spend limit per period on the organisation, a business area, a project, a team or a specialist.
          </ArtEmpty>
        ) : undefined
      }
    >
      {rows.length ? (
        <ListBlock flush>
          <div className="data-table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Budget</th>
                  <th className="w-40 @[40rem]:w-56">Spent of the limit</th>
                  <th className="data-table__end hidden w-36 @[48rem]:table-cell">Spent</th>
                  <th className="hidden w-40 @[56rem]:table-cell">Standing</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => {
                  const pct = Math.min(100, s.percent);
                  const pace = s.forecast ? Math.min(100, s.forecast.percent) : undefined;
                  return (
                    <tr key={s.budget.id}>
                      <td>
                        <span className="font-medium">{s.budget.name}</span>
                        <span className="data-table__meta">
                          {scopeWords(s)} · a {s.budget.period}
                        </span>
                      </td>
                      <td>
                        <div className="econ-meter" role="meter" aria-label={`${s.budget.name}: ${Math.round(s.percent)}% of its limit`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(pct)}>
                          <span className="econ-meter__spent" data-state={s.state} style={{ width: `${Math.max(pct > 0 ? 2 : 0, pct)}%` }} />
                          {pace !== undefined ? <span className="econ-meter__pace" style={{ left: `${pace}%` }} title={`At the last seven days' pace: ${Math.round(s.forecast!.percent)}% by the period's end`} /> : null}
                        </div>
                        <span className="data-table__meta">
                          {Math.round(s.percent)}%{s.forecast ? ` · on pace for ${Math.round(s.forecast.percent)}%` : ''}
                        </span>
                      </td>
                      <td className="data-table__end hidden @[48rem]:table-cell">
                        {format(s.spent.usd)}
                        <span className="data-table__meta">of {format(s.budget.amountUsd)}</span>
                      </td>
                      <td className="hidden @[56rem]:table-cell">
                        <StatusMark state={STATE_MARK[s.state]} label={s.forecast?.exceeds && s.state === 'ok' ? 'On pace to pass it' : BUDGET_STATE_TEXT[s.state]} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </ListBlock>
      ) : null}
    </ListGroup>
  );
}

// ── Breakdown ───────────────────────────────────────────────────────

function Breakdown({ tone, filters, by, onBy }: { tone: ArtTone; filters: ReturnType<typeof filtersOf>; by: CostGrouping; onBy: (by: CostGrouping) => void }) {
  const { format } = useMoney();
  const res = useLoad(() => routes.costs({ ...filters, by, top: 50 }).catch((x: unknown) => (forbidden(x) ? null : Promise.reject(x))), [filters, by], 120_000, { whenVisible: true });
  const d = res.data;
  const g = GROUPS[by];
  const measure = measureOf(d?.total);
  const options = Object.fromEntries(groupsHere().map((k) => [k, `By ${GROUPS[k].label.toLowerCase()}`])) as Record<CostGrouping, string>;
  if (d === null) return <ArtEmpty tone={tone} icon={<ChartColumn />} title="Costs are for administrators">Ask an administrator of your organisation.</ArtEmpty>;
  const named = (l: CostLine) => {
    if (l.rest) return <span className="text-muted">{l.name}</span>;
    if (!l.key) return <span className="text-muted">{l.name ?? g.none}</span>;
    const words = by === 'person' ? <SubjectName id={l.key} fallback={l.key} /> : by === 'session' ? <JobName r={l} /> : (l.name ?? l.key);
    const href = rankHref(by, l);
    return href ? (
      <Link to={href} className="hover:underline">
        {words}
      </Link>
    ) : (
      words
    );
  };
  return (
    <div className="econ">
      <ErrorNote error={res.error} />
      <ListGroup
        title={`By ${g.label.toLowerCase()}`}
        count={d ? d.lines.filter((l) => !l.rest).length : undefined}
        description={d?.records?.partial ? `Some calls in this period are older than the ${d.records.retentionDays} days of records kept, so this counts only the newer ones.` : 'Largest first. The share is of the spend; paid seats show at list price beside it.'}
        actions={<ChoiceSelect label="Group by" className="w-48 max-w-full" value={by} options={options} onChange={onBy} />}
        empty={d && d.lines.length === 0 ? <ArtEmpty tone={tone} icon={<Persons />} compact title="Nothing recorded in this period">Pick a longer period, or clear the filters.</ArtEmpty> : undefined}
      >
        {!d ? <ListBlock className="text-sm text-muted">Reading the costs…</ListBlock> : null}
        {d && d.lines.length ? (
          <ListBlock flush>
            <div className="data-table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{g.column}</th>
                    <th className="data-table__end w-32 @[40rem]:w-40">Spend</th>
                    <th className="data-table__end hidden w-32 @[48rem]:table-cell">Paid seats</th>
                    <th className="data-table__end hidden w-24 @[40rem]:table-cell">Tokens</th>
                    <th className="data-table__end hidden w-20 @[56rem]:table-cell">Calls</th>
                    <th className="data-table__end hidden w-20 @[64rem]:table-cell">Cache</th>
                    <th className="hidden w-36 @[56rem]:table-cell">Over the period</th>
                  </tr>
                </thead>
                <tbody>
                  {d.lines.map((l) => (
                    <tr key={l.rest ? '__rest' : l.key || '__none'}>
                      <td>
                        <span className="flex min-w-0 items-center gap-2">
                          {l.face ? <MemberFace member={l.face} /> : null}
                          <span className="min-w-0">
                            <span className={l.rest ? '' : 'font-medium'}>{named(l)}</span>
                            {l.detail && by !== 'session' ? <span className="data-table__meta">{l.detail}</span> : null}
                            {by === 'session' && l.detail ? <span className="data-table__meta">{l.detail}</span> : null}
                            {l.estimatedUsd ? <span className="data-table__meta">{format(l.estimatedUsd)} of it estimated until the Azure bill covers it</span> : null}
                          </span>
                        </span>
                      </td>
                      <td className="data-table__end">
                        {format(l.usd)}
                        <span className="data-table__meta">{l.share}%</span>
                      </td>
                      <td className="data-table__end hidden @[48rem]:table-cell">{l.subscriptionUsd ? format(l.subscriptionUsd) : '—'}</td>
                      <td className="data-table__end hidden @[40rem]:table-cell">{compact(l.tokens)}</td>
                      <td className="data-table__end hidden @[56rem]:table-cell">
                        {compact(l.calls)}
                        {l.unpricedCalls ? <span className="data-table__meta">{compact(l.unpricedCalls)} unpriced</span> : null}
                      </td>
                      <td className="data-table__end hidden @[64rem]:table-cell">{l.promptTokens ? percentWords((l.cacheReadTokens ?? 0) / l.promptTokens) : '—'}</td>
                      <td className="hidden @[56rem]:table-cell">
                        <Sparkline points={l.series} measure={measure} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </ListBlock>
        ) : null}
      </ListGroup>
      {!isCloud() ? <Outcomes since={filters.since} until={filters.until} {...(filters.areaId ? { areaId: filters.areaId } : {})} /> : null}
    </div>
  );
}
